import { useState, useEffect, useRef } from "react";
import ToggleSwitch from "./ui/ToggleSwitch";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { safeLocalStorage } from "../utils/safeStorage";
// INSTANCE PARTAGÉE obligatoire (bug prod 06/07/2026) : l'instance locale
// n'attachait pas X-CSRF-Token → « Sauvegarder une recherche » était rejeté
// 403 pour tout utilisateur web. Cf. DUPLICATE_GUARDRAILS.
import { api } from "../context/AuthContext";
// TAXONOMIE — import STATIQUE volontaire, et il ne coûte rien : elle est DÉJÀ
// dans le bundle d'accueil (HomePage importe `constants/categoriesProgrammatic`,
// qui importe ce même module). Vérifié sur le build du 23/09/2026 :
// `grep -l "gively-taxonomy/v1" build/static/js/*.js` -> main.js. On y lit le
// champ `types` (« quels types d'annonce cette catégorie autorise »), généré
// depuis le canonique `taxonomy/gively-taxonomy.json` et — jusqu'ici — lu par
// personne côté front.
import TAXONOMY from "../constants/taxonomy.generated";

// Les trois piliers montrés à l'utilisateur. « free » (don) existe aussi côté
// serveur (`KNOWN_LISTING_TYPES`) mais n'est pas un onglet de l'accueil
// (`HomePage.js` TYPE_TABS) : on ne propose pas dans la barre ce que la page
// d'arrivée ne sait pas filtrer.
const PILIERS_MONTRES = ["sale", "rental", "service"];
const LIBELLE_PILIER = { sale: "Vente", rental: "Location", service: "Services" };

// Icons
const SearchIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
  </svg>
);

const MapPinIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
  </svg>
);

const TagIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
  </svg>
);

const ClockIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const XIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const BellIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
  </svg>
);

// Smart Search Bar with Autocomplete
export const SmartSearchBar = ({ 
  placeholder = "Que cherchez-vous ?",
  onSearch,
  showLocationInput = true,
  className = ""
}) => {
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const locationRef = useRef(null);
  // DEUX MINUTEURS, PAS UN. Un unique `debounceRef` était partagé par le champ
  // texte et le champ ville : taper dans « Où ? » annulait la requête
  // d'autocomplétion en vol, et réciproquement. Une suggestion sur deux ne
  // partait jamais, et le défaut était invisible parce qu'il ressemblait à de
  // la lenteur réseau.
  const debounceQueryRef = useRef(null);
  const debounceLocationRef = useRef(null);
  // ANTI-CHEVAUCHEMENT. Aucun ordre d'arrivée n'était garanti : une réponse
  // lente pour « ve » pouvait écraser une réponse rapide pour « velo », et
  // l'utilisateur voyait des suggestions qui ne correspondaient plus à ce
  // qu'il avait tapé. On numérote les requêtes et on jette les périmées.
  // (Règle permanente « race conditions dès la conception » : ce risque
  // grandit avec chaque source de suggestions ajoutée.)
  const seqRef = useRef(0);
  // MOTEUR DE CLASSEMENT ET FACETTES — chargés UNE fois, au premier focus.
  // Le moteur (37 Ko source, ~13 Ko gzip) n'est PAS dans le bundle d'accueil
  // et ne doit pas y entrer : un `import()` le fait payer par qui cherche,
  // jamais par qui passe. Les facettes disent où il y a réellement du stock.
  const moteurRef = useRef(null);
  const facettesRef = useRef(null);
  const chargementLanceRef = useRef(false);
  const [locales, setLocales] = useState([]);
  const [actif, setActif] = useState(-1);

  // Load recent searches from localStorage. safeLocalStorage : ne jette jamais
  // (iOS Safari navigation privée → localStorage brut peut throw = écran blanc).
  useEffect(() => {
    try {
      const saved = safeLocalStorage.getItem("recentSearches");
      if (saved) setRecentSearches(JSON.parse(saved));
    } catch { /* données corrompues : on ignore */ }
  }, []);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
      if (locationRef.current && !locationRef.current.contains(e.target)) {
        setShowLocationSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch search suggestions
  const fetchSuggestions = async (q) => {
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const monTour = ++seqRef.current;
    setLoading(true);
    try {
      const response = await api.get(`/search/autocomplete?q=${encodeURIComponent(q)}&limit=8`);
      if (monTour !== seqRef.current) return;   // réponse périmée : on la jette
      setSuggestions(response.data);
    } catch (error) {
    } finally {
      if (monTour === seqRef.current) setLoading(false);
    }
  };

  /**
   * Charge, UNE seule fois, le moteur de classement et les facettes.
   *
   * POURQUOI AU FOCUS ET PAS AU MONTAGE : la page d'accueil se charge pour tout
   * le monde ; la barre ne sert qu'à ceux qui cherchent. Déclencher au focus,
   * c'est faire payer le coût à l'usage. Les deux chargements sont
   * indépendants et TOLÉRANTS À L'ÉCHEC : si le moteur ne vient pas, la barre
   * garde les suggestions du serveur ; si les facettes ne viennent pas, elle
   * n'affiche simplement aucune rubrique — jamais une fausse.
   */
  const chargerLeMoteur = () => {
    if (chargementLanceRef.current) return;
    chargementLanceRef.current = true;
    import("../pages/publish/state/classifyTitle")
      .then((m) => { moteurRef.current = m.classifyTitle; })
      .catch(() => { moteurRef.current = null; });
    api.get("/search/facettes")
      .then((r) => { facettesRef.current = r?.data && typeof r.data === "object" ? r.data : {}; })
      .catch(() => { facettesRef.current = {}; });
  };

  /**
   * Suggestions « <requête> dans <Rubrique> · <Pilier> », calculées LOCALEMENT.
   *
   * DEUX FILTRES, ET AUCUN DES DEUX N'EST DÉCORATIF :
   *
   *  1. LE PILIER AUTORISÉ (`TAXONOMY.categories[x].types`). Pour les véhicules
   *     il vaut `["sale"]` — c'est la forme déclarative de
   *     `SALE_ONLY_CATEGORIES` (backend/services/listing_guards.py:224),
   *     décision de Lucas de juillet 2026 : « les véhicules ne peuvent être que
   *     vendus — l'assurance, la responsabilité et les sinistres rendent la
   *     location entre particuliers inadaptée sur Gively ». Leboncoin peut
   *     afficher « voiture dans Location » parce qu'il AUTORISE la location de
   *     voitures ; nous non. Proposer la combinaison ferait croire que Gively
   *     loue des voitures, et mènerait à une page que rien ne peut remplir.
   *
   *  2. LE STOCK RÉEL (`facettes[cat][pilier] > 0`). Baymard Institute,
   *     « Autodirect or Guide Users to Matching Category Scopes »
   *     (21/01/2020, https://baymard.com/blog/autodirect-searches-matching-category-scopes,
   *     consulté le 23/09/2026) recommande d'éliminer les suggestions de portée
   *     quand il y a moins de dix résultats. Gively en est là : 46 annonces
   *     actives, 12 couples catégorie × type peuplés. On ne renonce pas à la
   *     fonction — on ne propose que ce qui existe. Une suggestion qui mène à
   *     « Aucune annonce trouvée » est pire que pas de suggestion : c'est la
   *     plateforme qui a orienté l'utilisateur vers le vide.
   */
  const suggestionsLocales = (q) => {
    const classer = moteurRef.current;
    const facettes = facettesRef.current;
    if (!classer || !facettes || String(q || "").trim().length < 2) return [];
    let classement;
    try {
      classement = classer(q, {});
    } catch { return []; }
    const out = [];
    for (const cand of (classement?.alternatives || []).slice(0, 3)) {
      const cat = TAXONOMY.categories?.[cand.category];
      const autorises = Array.isArray(cat?.types) ? cat.types : [];
      const comptes = facettes[cand.category] || {};
      for (const pilier of PILIERS_MONTRES) {
        if (!autorises.includes(pilier)) continue;        // interdit à la publication
        const n = comptes[pilier] || 0;
        if (n <= 0) continue;                              // rubrique sans stock
        out.push({
          type: "scope",
          text: q,
          label: cand.label,
          listing_type: pilier,
          pilier_label: LIBELLE_PILIER[pilier],
          category_id: cand.category,
          subcategory_id: cand.subcategory || null,
          count: n,
        });
      }
    }
    return out.slice(0, 3);
  };

  // Fetch location suggestions
  const fetchLocationSuggestions = async (q) => {
    if (q.length < 2) {
      setLocationSuggestions([]);
      return;
    }
    
    try {
      const response = await fetch(
        `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(q)}&limit=5&fields=nom,codesPostaux,departement`
      );
      const data = await response.json();
      setLocationSuggestions(data);
    } catch (error) {
    }
  };

  // Debounced search — minuteur PROPRE au champ texte (cf. debounceQueryRef).
  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setActif(-1);

    if (debounceQueryRef.current) {
      clearTimeout(debounceQueryRef.current);
    }

    debounceQueryRef.current = setTimeout(() => {
      fetchSuggestions(value);
      setLocales(suggestionsLocales(value));
    }, 300);
  };

  // Debounced location search — minuteur PROPRE au champ ville.
  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocation(value);

    if (debounceLocationRef.current) {
      clearTimeout(debounceLocationRef.current);
    }

    debounceLocationRef.current = setTimeout(() => {
      fetchLocationSuggestions(value);
    }, 300);
  };

  // Handle search submit.
  // Audit LUCAS — overrideQuery/overrideLocation permettent de lancer la
  // recherche avec une valeur fraîche SANS attendre le re-render de setQuery
  // (sinon, au clic sur une recherche récente / suggestion, handleSearch lisait
  // l'ANCIEN `query` du closure et partait avec la mauvaise valeur).
  const handleSearch = (e, overrideQuery, overrideLocation) => {
    e?.preventDefault();
    const q = overrideQuery !== undefined ? overrideQuery : query;
    const loc = overrideLocation !== undefined ? overrideLocation : location;

    if (q.trim() || loc.trim()) {
      // Save to recent searches
      if (q.trim()) {
        const newRecent = [
          { query: q, location: loc, timestamp: Date.now() },
          ...recentSearches.filter(r => r.query !== q).slice(0, 4)
        ];
        setRecentSearches(newRecent);
        safeLocalStorage.setItem("recentSearches", JSON.stringify(newRecent));
      }
    }

    setShowSuggestions(false);
    setShowLocationSuggestions(false);

    if (onSearch) {
      onSearch({ query: q, location: loc });
    } else {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (loc) params.set("city", loc);
      navigate(`/search?${params.toString()}`);
    }
  };

  // Select suggestion
  const selectSuggestion = (suggestion) => {
    setShowSuggestions(false);
    setActif(-1);

    // VILLE — on ne remplace PAS la recherche par le nom de la ville : on
    // remplit le champ « Où ? » et on garde ce que l'utilisateur cherchait.
    // Taper « toulouse » dans la barre rendait zéro suggestion alors que 37 de
    // nos 46 annonces y sont (relevé du 23/09/2026) ; les proposer en écrasant
    // la requête aurait juste déplacé le problème.
    if (suggestion.type === "city") {
      setLocation(suggestion.text);
      handleSearch(null, query, suggestion.text);
      return;
    }

    setQuery(suggestion.text);

    // RUBRIQUE AVEC PILIER — « perceuse dans Maison › Bricolage · Location ».
    // On transporte aussi le texte cherché : la rubrique CADRE la recherche,
    // elle ne la remplace pas.
    if (suggestion.type === "scope") {
      const params = new URLSearchParams();
      params.set("q", suggestion.text);
      params.set("category", suggestion.category_id);
      if (suggestion.subcategory_id) {
        params.set("subcategory", suggestion.subcategory_id);
      }
      if (suggestion.listing_type) params.set("listing_type", suggestion.listing_type);
      navigate(`/search?${params.toString()}`);
      return;
    }

    if (suggestion.type === "category" || suggestion.type === "subcategory") {
      const params = new URLSearchParams();
      params.set("category", suggestion.category_id);
      if (suggestion.subcategory_id) {
        params.set("subcategory", suggestion.subcategory_id);
      }
      navigate(`/search?${params.toString()}`);
    } else {
      handleSearch(null, suggestion.text);
    }
  };

  /**
   * La liste affichée : suggestions du SERVEUR puis rubriques calculées
   * LOCALEMENT, dédoublonnées, bornée à 8.
   *
   * L'ordre reflète celui dans lequel une personne décide : l'objet cherché
   * d'abord (annonces), puis où le chercher (rubriques, villes).
   */
  const listeAffichee = (() => {
    const vues = new Set();
    const out = [];
    for (const s of [...suggestions, ...locales]) {
      const cle = `${s.type}:${s.text}:${s.category_id || ""}:${s.subcategory_id || ""}:${s.listing_type || ""}`;
      if (vues.has(cle)) continue;
      vues.add(cle);
      out.push(s);
    }
    return out.slice(0, 8);
  })();

  /**
   * Clavier — motif « combobox » du W3C ARIA Authoring Practices
   * (https://www.w3.org/WAI/ARIA/apg/patterns/combobox/, consulté le
   * 23/09/2026) : flèches pour parcourir, Entrée pour accepter, Échap pour
   * fermer, le focus DOM restant sur le champ.
   *
   * POURQUOI C'EST DANS CE LOT et pas « plus tard » : la liste n'avait AUCUNE
   * navigation clavier ni aucun rôle ARIA — au clavier et au lecteur d'écran,
   * ces suggestions n'existaient pas. Enrichir une liste qu'on ne peut pas
   * parcourir aggrave le défaut au lieu de le corriger : on ajoute du contenu
   * inatteignable. Le gabarit est repris de `components/AddressAutocomplete.js`,
   * déjà en production dans ce dépôt.
   */
  const onKeyDownQuery = (e) => {
    if (!showSuggestions || listeAffichee.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActif((i) => (i + 1) % listeAffichee.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActif((i) => (i <= 0 ? listeAffichee.length - 1 : i - 1));
    } else if (e.key === "Enter" && actif >= 0) {
      e.preventDefault();
      selectSuggestion(listeAffichee[actif]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setActif(-1);
    }
  };

  // Select location
  const selectLocation = (loc) => {
    setLocation(loc.nom);
    setShowLocationSuggestions(false);
  };

  return (
    <form onSubmit={handleSearch} className={`flex flex-col sm:flex-row gap-2 ${className}`}>
      {/* Search Input */}
      <div className="relative flex-1" ref={searchRef}>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={handleQueryChange}
            onFocus={() => { setShowSuggestions(true); chargerLeMoteur(); }}
            onKeyDown={onKeyDownQuery}
            placeholder={placeholder}
            aria-label={placeholder || "Que cherchez-vous ?"}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showSuggestions && listeAffichee.length > 0}
            aria-controls="smart-search-listbox"
            aria-activedescendant={actif >= 0 ? `smart-search-option-${actif}` : undefined}
            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-[#1DA1F2] focus:ring-2 focus:ring-[#1DA1F2]/20"
            data-testid="smart-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(""); setSuggestions([]); setLocales([]); setActif(-1); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full"
              aria-label="Effacer la recherche"
            >
              <XIcon className="w-4 h-4 text-slate-400" aria-hidden="true" />
            </button>
          )}
        </div>
        
        {/* Suggestions Dropdown */}
        {showSuggestions && (listeAffichee.length > 0 || recentSearches.length > 0) && (
          <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-lg border border-slate-200 max-h-80 overflow-y-auto">
            {/* Recent searches */}
            {!query && recentSearches.length > 0 && (
              <div className="p-2 border-b">
                <p className="text-xs font-semibold text-slate-500 px-2 mb-1">Recherches récentes</p>
                {recentSearches.map((recent, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setQuery(recent.query); setLocation(recent.location || ""); handleSearch(null, recent.query, recent.location || ""); }}
                    className="w-full flex items-center gap-2 px-2 py-2 hover:bg-slate-50 rounded-lg text-left"
                  >
                    <ClockIcon className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-700">{recent.query}</span>
                    {recent.location && (
                      <span className="text-xs text-slate-400">• {recent.location}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            
            {/* Autocomplete suggestions */}
            {listeAffichee.length > 0 && (
              <div className="p-2" role="listbox" id="smart-search-listbox">
                {listeAffichee.map((suggestion, i) => (
                  <button
                    key={`${suggestion.type}-${suggestion.text}-${suggestion.category_id || ""}-${suggestion.listing_type || ""}`}
                    id={`smart-search-option-${i}`}
                    role="option"
                    aria-selected={i === actif}
                    type="button"
                    onClick={() => selectSuggestion(suggestion)}
                    onMouseEnter={() => setActif(i)}
                    className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left ${
                      i === actif ? "bg-slate-100" : "hover:bg-slate-50"
                    }`}
                    data-testid={`smart-search-suggestion-${suggestion.type}`}
                  >
                    {suggestion.type === "city" ? (
                      <MapPinIcon className="w-4 h-4 text-emerald-600" aria-hidden="true" />
                    ) : suggestion.type === "listing" ? (
                      <SearchIcon className="w-4 h-4 text-slate-400" aria-hidden="true" />
                    ) : (
                      <TagIcon className="w-4 h-4 text-[#1DA1F2]" aria-hidden="true" />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-slate-700">{suggestion.text}</span>
                      {/* « <requête> dans <Rubrique> · <Pilier> » — la forme
                          demandée par Lucas. `label` vient de classifyTitle et
                          vaut déjà « Catégorie › Sous-catégorie ». */}
                      {suggestion.type === "scope" && (
                        <span className="text-xs text-slate-500 ml-1">
                          dans {suggestion.label} · {suggestion.pilier_label}
                        </span>
                      )}
                      {suggestion.price != null && (
                        <span className="text-xs text-slate-400 ml-2">~{suggestion.price}€</span>
                      )}
                    </div>
                    {/* Le compte est AFFICHÉ, jamais promis à vide : une rubrique
                        n'arrive dans cette liste que si elle a du stock réel
                        (cf. suggestionsLocales). L'utilisateur sait donc ce qui
                        l'attend avant de cliquer. */}
                    {suggestion.type === "scope" && (
                      <span className="text-xs bg-[#1DA1F2]/10 text-[#1DA1F2] px-2 py-0.5 rounded-full shrink-0">
                        {suggestion.count}
                      </span>
                    )}
                    {suggestion.type === "city" && (
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full shrink-0">
                        {suggestion.count} annonce{suggestion.count > 1 ? "s" : ""}
                      </span>
                    )}
                    {/* Le badge disait « Catégorie » y compris pour une
                        SOUS-catégorie : on nomme désormais ce qu'on montre. */}
                    {(suggestion.type === "category" || suggestion.type === "subcategory") && (
                      <span className="text-xs bg-[#1DA1F2]/10 text-[#1DA1F2] px-2 py-0.5 rounded-full shrink-0">
                        {suggestion.type === "subcategory" ? "Sous-catégorie" : "Catégorie"}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Location Input */}
      {showLocationInput && (
        <div className="relative sm:w-48" ref={locationRef}>
          <div className="relative">
            <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={location}
              onChange={handleLocationChange}
              onFocus={() => setShowLocationSuggestions(true)}
              placeholder="Où ?"
              aria-label="Ville ou code postal"
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-[#1DA1F2] focus:ring-2 focus:ring-[#1DA1F2]/20"
              data-testid="location-input"
            />
          </div>
          
          {/* Location Suggestions */}
          {showLocationSuggestions && locationSuggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-lg border border-slate-200 max-h-60 overflow-y-auto">
              {locationSuggestions.map((loc, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => selectLocation(loc)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left"
                >
                  <MapPinIcon className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-700">{loc.nom}</p>
                    <p className="text-xs text-slate-400">
                      {loc.codesPostaux?.[0]} - {loc.departement?.nom}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search Button — icône + libellé sur TOUTES les tailles : sur mobile le
          bouton est pleine largeur (flex-col), une icône seule y flottait sans
          sens (retour Lucas, août 2026). */}
      <button
        type="submit"
        className="flex items-center justify-center gap-2 px-6 py-3 bg-[#1DA1F2] text-white font-semibold rounded-xl hover:bg-[#0d8ecf] transition-colors"
        data-testid="search-submit"
      >
        <SearchIcon className="w-5 h-5" aria-hidden="true" />
        <span>Rechercher</span>
      </button>
    </form>
  );
};

// Save Search Modal — « Créer une alerte » (30/09/2026).
// Elle existait mais n'était montée NULLE PART : aucune recherche ne pouvait
// être enregistrée depuis l'interface. Montée désormais par SearchPage.
// Nom proposé d'office (on peut le changer), interrupteurs au lieu de cases,
// et le TYPE d'annonce (vente / location / service) enregistré avec le reste.
export const SaveSearchModal = ({
  isOpen,
  onClose,
  searchParams = {},
}) => {
  const nomPropose = [searchParams.query, searchParams.location].filter(Boolean).join(" · ") || "Nouvelles annonces";
  const [name, setName] = useState(nomPropose);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyPush, setNotifyPush] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setName(nomPropose);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Donnez un nom à votre recherche.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/saved-searches", {
        name: name.trim(),
        query: searchParams.query || "",
        category: searchParams.category || null,
        subcategory: searchParams.subcategory || null,
        location: searchParams.location || null,
        price_min: searchParams.priceMin ? Number(searchParams.priceMin) : null,
        price_max: searchParams.priceMax ? Number(searchParams.priceMax) : null,
        filters: searchParams.listingType ? { listing_type: searchParams.listingType } : {},
        notify_email: notifyEmail,
        notify_push: notifyPush,
      });
      toast.success("Alerte créée : vous serez prévenu des nouvelles annonces. Retrouvez-la dans « Mes recherches ».");
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "L'alerte n'a pas pu être créée. Réessayez.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="save-search-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-safe overflow-y-auto" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="save-search-titre">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-sky-50 text-[#1DA1F2]" aria-hidden="true">
            <BellIcon className="w-5 h-5" />
          </span>
          <h2 id="save-search-titre" className="text-lg font-bold text-slate-900">Créer une alerte</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Vous serez prévenu dès qu'une nouvelle annonce correspond à cette recherche.
        </p>

        <div className="bg-slate-50 rounded-xl p-3 mb-4 text-sm text-slate-700">
          <p className="font-medium">{searchParams.query || "Tous les mots"}</p>
          {searchParams.location && (
            <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
              <MapPinIcon className="w-3 h-3" />
              {searchParams.location}
            </p>
          )}
        </div>

        <label htmlFor="save-search-nom" className="block text-sm font-medium text-slate-700 mb-1">Nom de l'alerte</label>
        <input
          id="save-search-nom"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl mb-4 focus:outline-none focus:border-[#1DA1F2]"
          data-testid="search-name-input"
        />

        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-slate-700">Notification dans l'application</span>
            <ToggleSwitch checked={notifyPush} onChange={() => setNotifyPush((v) => !v)} label="Notification dans l'application" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-slate-700">E-mail</span>
            <ToggleSwitch checked={notifyEmail} onChange={() => setNotifyEmail((v) => !v)} label="Alerte par e-mail" />
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 border border-slate-200 rounded-xl text-slate-700 font-medium hover:bg-slate-50">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex-1 py-3 bg-[#1DA1F2] text-white font-semibold rounded-xl hover:bg-[#0d8ecf] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="save-search-btn"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <BellIcon className="w-5 h-5" />
                Créer l'alerte
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SmartSearchBar;
